/**
 * <s-tabs>
 *   <div class="tab__triggers" role="tablist" aria-orientation="horizontal" js-tablist>
 *     <button id="tab1-tab" class="tab__trigger" aria-controls="tab1-content-panel" role="tab" aria-selected="true" js-tab>Tab1</button>
 *     <button id="tab2-tab" class="tab__trigger" aria-controls="tab2-content-panel" role="tab" aria-selected="false" js-tab>Tab2</button>
 *     <button id="tab3-tab" class="tab__trigger" aria-controls="tab3-content-panel" role="tab" aria-selected="false" js-tab>Tab3</button>
 *   </div>
 *   <div id="tab1-content-panel" class="tab__panel" role="tabpanel" aria-labelledby="tab1-tab" aria-hidden="false"></div>
 *   <div id="tab2-content-panel" class="tab__panel" role="tabpanel" aria-labelledby="tab2-tab" aria-hidden="true"></div>
 *   <div id="tab3-content-panel" class="tab__panel" role="tabpanel" aria-labelledby="tab3-tab" aria-hidden="true"></div>
 * </s-tabs>
 **/

class Tabs extends HTMLElement {
    constructor() {
      super();
    }
  
    connectedCallback() {
      this._elements = {
        tabs: this.querySelectorAll('[js-tab]'),
        tabList: this.querySelectorAll('[js-tab-list]'),
        tabPanel: this.querySelectorAll('[js-tab-panel]')
      };
  
  
      this._tabEventListener();
      this._tabListListener();
    }
  
  
    _tabEventListener = () => {
      const { tabs } = this._elements;
      tabs.forEach((tab) =>
        tab.addEventListener("click", this._showTabPanel)
      );
    }
  
    _showTabPanel = (el) => {
      const { tabs } = this._elements;
      for (let i = 0; i < tabs.length; i++) {
        tabs[i].setAttribute('aria-selected', 'false');
      }
      el.target.setAttribute('aria-selected', 'true');
      var tabPanelToOpen = el.target.getAttribute('aria-controls');
      var tabPanels = this.querySelectorAll('[role=tabpanel]');
      for (let i = 0; i < tabPanels.length; i++) {
        tabPanels[i].setAttribute('aria-hidden', 'true');
      }
      const targetPanel = document.getElementById(tabPanelToOpen);
      if (targetPanel) {
        targetPanel.setAttribute('aria-hidden', 'false');
      }
    }
  
    _tabListListener = () => {
      const { tabList } = this._elements;
      tabList.forEach((list) =>
        list.addEventListener("keydown", this._tabListKeydown)
      );
    }
  
    _tabListKeydown = (e) => {
      const currentTab = this.querySelector('[aria-selected="true"]');
      if (!currentTab) return;
      
      if (e.keyCode == 37 || e.keyCode == 38) {
        const prevTab = currentTab.previousElementSibling;
        if (prevTab && prevTab.hasAttribute('js-tab')) {
          prevTab.click();
          prevTab.focus();
          e.preventDefault();
        }
      }
      if (e.keyCode == 39 || e.keyCode == 40) {
        const nextTab = currentTab.nextElementSibling;
        if (nextTab && nextTab.hasAttribute('js-tab')) {
          nextTab.click();
          nextTab.focus();
          e.preventDefault();
        }
      }
    }
  
  }

customElements.define("s-tabs", Tabs);

/**
 * Carousel Component
 * --------------------------------------------------------------------
 * @summary carousel, uses swiper, dots are optional, and arrows are optional
 *
 * See https://swiperjs.com/swiper-api#parameters for more carousel options
 *
 * @usage
 * 
 *     {%- capture carousel_options -%}
 *       {
 *         "slidesPerView": "auto",
 *         "loop": false,
 *         ...
 *       }
 *     {%- endcapture -%}
 *      <s-carousel data-options='{{carousel_options}}' data-init="{{breakpoint}}">
 *        <div id="{{custom_id}}" class="swiper" js-carousel>
 *          <div class="swiper-wrapper">
 *            <div class="swiper-slide" js-carousel-slide></div>
 *            ...
 *          </div>
 *          <div class="swiper-button-prev {{prev_button_class}}"></div>
 *          <div class="swiper-button-next {{next_button_class}}"></div>
 *          <div class="swiper-pagination {{pagination_class}}"></div>
 *        </div>
 *      </s-carousel>
 *
 * The following parameters are attributes that the <s-carousel> element takes
 * @param data-options - object containing swiper options
 *                       ex.{"slidesPerView":"auto", "loop":false}
 *                       only needed if modifying or extending on default options
 *                       be careful with syntax here you have to use single quote on the outside,
 *                       and double quotes in the capture so that the JSON.parse() doesn't error.
 *                       DEFAULT OPTIONS:
 *                          loop: true,
 *                          slidesPerView: 'auto',
 *                          grabCursor: true,
 *                          centeredSlides: false,
 *                          on: {init: this._onReady},
 * 
 * @param data-init - use to initialize or destroy carousel at certain widths.
 *                    pass in variable from theme.config.mediaQueries (/global/config.js)
 *                    
 *                    enquire match/unmatch is pretty specific, so follow the rules below:
 *                    if mobile = destroyed, higher width = initialized, use a max-width variable
 *                    if mobile = intialized, higher width = destroyed, use a min-width variable
 * 
 *                    if breakpoint does not exist, feel free to add to config.js
 *
 * The following are selectors that the <s-carousel> searches for
 * [js-carousel] - required, this contains the slides and the arrows.
 * id - required, should be same element as [js-carousel]
 * .swiper - required class
 * .swiper-wrapper - required class
 * [js-carousel-slide] - required, each slide element needs this (differentiates from the arrows)
 * .swiper-slide - required class
 * .swiper-pagination - optional, custom {{pagination_class}} required if using pagination
 * .swiper-button-prev - optional, custom {{prev_button_class}} required if using navigation
 * .swiper-button-next - optional, custom {{next_button_class}} required if using navigation
 */

theme.config = {
  mediaQueries: {
    landscapeAll: 'screen and (orientation: landscape)',
    landscape: 'screen and (orientation: landscape), screen and (min-width: 48rem)',
    belowTablet: 'screen and (max-width: 47.99rem)',
    tablet: 'screen and (min-width: 48rem)',
    belowLaptop: 'screen and (max-width: 63.99rem)',
    laptop: 'screen and (min-width: 64rem)',
    belowDesktop: 'screen and (max-width: 64rem)',
    desktop: 'screen and (min-width: 64.01rem)',
    belowWidescreen: 'screen and (max-width: 79.99rem)',
    widescreen: 'screen and (min-width: 80rem)',
    belowExtrawide: 'screen and (max-width: 89.99rem)',
    extrawide: 'screen and (min-width: 90rem)'
  },
};

class Carousel extends HTMLElement {
    constructor() {
      super();
  
      this.selectors = {
        carousel: '[js-carousel]'
      }
  
      this.options = {
        loop: true,
        slidesPerView: 'auto',
        grabCursor: true,
        centeredSlides: false,
        navigation: false,
        pagination: false,
      }
    }
  
    connectedCallback() {
      const extendedOptions = this.dataset.options ? JSON.parse(this.dataset.options) : {}
      this.options = Object.assign(this.options, extendedOptions)
      this._initCarousel();
    }
  
    // Update data-slide attribute
    _updateSlideData(swiper) {
      this.dataset.slide = swiper.activeIndex + 1;
      this.dataset.totalSlides = swiper.slides.length;
    }

    // Initialize carousel
    _initCarousel() {
      let id = `#${this.querySelector(this.selectors.carousel).id}`;
      const self = this;
      if (typeof this.dataset.init !== 'undefined') {
        //do nothing initially, init at larger points
        enquire.register(theme.config.mediaQueries[this.dataset.init],{
          match: function(){
            this.carousel = new Swiper(id, this.options);
            this._updateSlideData(this.carousel);
            this.carousel.on('slideChange', () => this._updateSlideData(this.carousel));
          }.bind(this),
          unmatch: function(){
            if (this.carousel.el.classList.contains('swiper-initialized')) {
              this.carousel.destroy(true, true);
            }
          }.bind(this)
        })
      }else{
        this.carousel = new Swiper(id, this.options);
        this._updateSlideData(this.carousel);
        this.carousel.on('slideChange', () => this._updateSlideData(this.carousel));
      }
    }
  }

  customElements.define("s-carousel", Carousel);

/**
 * Same-page anchor links: smooth scroll (see seed-kith global.js).
 * Offset uses theme --header-height plus extra padding so targets are not tucked under the header.
 */
function getAnchorScrollOffset() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
  let headerPx = parseFloat(raw, 10) || 0;
  if (!headerPx) {
    const headerEl = document.querySelector('[data-header-height]');
    if (headerEl) {
      headerPx = headerEl.getBoundingClientRect().height;
    }
  }
  const extraPadding = 32;
  return headerPx + extraPadding;
}

function normalizeRangeScrollId(id) {
  if (!id) return '';
  return id
    .toString()
    .trim()
    .replace(/^#/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function findRangeScrollTarget(id) {
  if (!id) return null;

  const candidates = [id];
  const normalized = normalizeRangeScrollId(id);
  if (normalized && normalized !== id) {
    candidates.push(normalized);
  }

  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    const escaped =
      typeof CSS !== 'undefined' && CSS.escape
        ? CSS.escape(candidate)
        : candidate.replace(/[^\w-]/g, '\\$&');

    const target =
      document.querySelector(
        `section.index-tab-collections[data-range-scroll-target="${candidate}"]`
      ) ||
      document.querySelector(`section.index-tab-collections#${escaped}`) ||
      document.getElementById(candidate);

    if (target) return target;
  }

  return null;
}

function scrollToRangeTarget(target) {
  const offset = getAnchorScrollOffset();
  const top =
    target.getBoundingClientRect().top + window.pageYOffset - offset;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

function runWhenRangesLpReady(callback) {
  if (document.body.classList.contains('is-loaded')) {
    callback();
    return;
  }

  const observer = new MutationObserver(() => {
    if (document.body.classList.contains('is-loaded')) {
      observer.disconnect();
      callback();
    }
  });
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
}

function initRangesLpHashScroll() {
  if (!document.body.classList.contains('template-ranges-lp')) return;

  const hash = window.__rangesLpPendingHash || window.location.hash;
  if (!hash || hash.length < 2) return;

  let id;
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch (e) {
    id = hash.slice(1);
  }

  const target = findRangeScrollTarget(id);
  if (!target) return;

  runWhenRangesLpReady(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToRangeTarget(target);

        if (window.__rangesLpPendingHash) {
          history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search + hash
          );
          delete window.__rangesLpPendingHash;
        }
      });
    });
  });
}

initRangesLpHashScroll();

document.addEventListener(
  'click',
  function (e) {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;

    const scrollId =
      link.getAttribute('data-scroll-target') ||
      (href.length > 1 ? href.slice(1) : '');

    if (href === '#') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!scrollId) return;

    const rangeTarget = findRangeScrollTarget(scrollId);
    if (rangeTarget) {
      e.preventDefault();
      scrollToRangeTarget(rangeTarget);
      return;
    }

    const genericTarget = document.querySelector(href);
    if (genericTarget) {
      e.preventDefault();
      scrollToRangeTarget(genericTarget);
    }
  },
  true
);

function initRangeBrowseBackButtons(scope) {
  const root = scope || document;
  root.querySelectorAll('[data-range-browse-back]').forEach((btn) => {
    if (btn.hasAttribute('data-range-browse-initialized')) return;
    btn.setAttribute('data-range-browse-initialized', '');

    const sectionId = btn.getAttribute('data-range-browse-section-id');
    const listSection = sectionId
      ? document.querySelector(
          `section[data-range-az-section][data-section-id="${sectionId}"]`
        )
      : btn.closest('section[data-range-az-section]');
    const scrollTarget =
      (sectionId && document.getElementById(`RangeAzNav--${sectionId}`)) ||
      listSection;

    if (!listSection || !scrollTarget) return;

    let ticking = false;

    function updateVisibility() {
      ticking = false;
      const show =
        listSection.getBoundingClientRect().bottom < getAnchorScrollOffset();
      btn.classList.toggle('is-visible', show);
      btn.setAttribute('aria-hidden', show ? 'false' : 'true');
      btn.tabIndex = show ? 0 : -1;
    }

    function onScrollOrResize() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateVisibility);
    }

    btn.addEventListener('click', () => {
      scrollToRangeTarget(scrollTarget);
    });

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    updateVisibility();
  });
}

initRangeBrowseBackButtons(document);
document.addEventListener('shopify:section:load', (ev) => {
  if (ev.target) initRangeBrowseBackButtons(ev.target);
});